import { useCallback, useRef, useState } from "react";
import type { MapLayoutIssueSelection } from "@/lib/office/map-layout-validate";
import type { MapLayoutDocument } from "@/lib/office/personal-office-layout";
import { cn } from "@/lib/utils";
import {
  CELL,
  type EditorItemRef,
  type EditorSelectionSet,
  expandBuildingGroup,
  getItemBounds,
  isSelected,
  itemsInRect,
  moveItems,
  type PaletteItem,
  paletteGhostSize,
  placeFurniture,
  placeOffice,
  placeOutdoorBuilding,
  placeSpawn,
  placeTransition,
  placeZone,
  resizeItemSe,
  sameItemRef,
} from "./map-layout-utils";

type Props = {
  layout: MapLayoutDocument;
  selection: EditorSelectionSet;
  palette: PaletteItem | null;
  issueSelections?: MapLayoutIssueSelection[];
  /**
   * Discrete edit (place item). Parent should push undo history from current layout.
   */
  onEdit: (layout: MapLayoutDocument) => void;
  /** Continuous edits during drag/resize (no history push). */
  onLiveChange: (layout: MapLayoutDocument) => void;
  /**
   * End of a drag/resize gesture that changed the layout.
   * Receives the layout snapshot from before the gesture (for undo).
   */
  onGestureEnd?: (before: MapLayoutDocument) => void;
  onSelect: (sel: EditorSelectionSet) => void;
};

type DragMode =
  | {
      type: "move";
      refs: EditorSelectionSet;
      lastGx: number;
      lastGy: number;
      startLayout: MapLayoutDocument;
      dirty: boolean;
    }
  | {
      type: "marquee";
      startGx: number;
      startGy: number;
      endGx: number;
      endGy: number;
      additive: boolean;
    }
  | {
      type: "resize";
      ref: EditorItemRef;
      startLayout: MapLayoutDocument;
      dirty: boolean;
    };

function issueKey(s: MapLayoutIssueSelection): string {
  if (s.kind === "spawn") return `spawn:${s.index}`;
  return `${s.kind}:${s.id}`;
}

export function MapLayoutCanvas({
  layout,
  selection,
  palette,
  issueSelections = [],
  onEdit,
  onLiveChange,
  onGestureEnd,
  onSelect,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragMode | null>(null);
  const [hover, setHover] = useState<{ gx: number; gy: number } | null>(null);

  const issueKeys = new Set(issueSelections.map(issueKey));
  const hasIssue = (ref: EditorItemRef) => issueKeys.has(issueKey(ref));

  const toGrid = useCallback(
    (clientX: number, clientY: number) => {
      const el = wrapRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const gx = Math.floor((clientX - rect.left + el.scrollLeft) / CELL);
      const gy = Math.floor((clientY - rect.top + el.scrollTop) / CELL);
      if (gx < 0 || gy < 0 || gx >= layout.cols || gy >= layout.rows) return null;
      return { gx, gy };
    },
    [layout.cols, layout.rows],
  );

  const hitTest = useCallback(
    (gx: number, gy: number): EditorItemRef | null => {
      for (const t of layout.transitions ?? []) {
        if (t.gx === gx && t.gy === gy) return { kind: "transition", id: t.id };
      }
      for (let i = layout.furniture.length - 1; i >= 0; i--) {
        const f = layout.furniture[i];
        if (!f) continue;
        if (gx >= f.gx && gx < f.gx + f.w && gy >= f.gy && gy < f.gy + f.d) {
          return { kind: "furniture", id: f.id };
        }
      }
      for (const o of layout.offices) {
        if (gx >= o.x1 && gx <= o.x2 && gy >= o.y1 && gy <= o.y2) {
          return { kind: "office", id: o.slotKey };
        }
      }
      for (const z of layout.commonZones) {
        if (gx >= z.x1 && gx <= z.x2 && gy >= z.y1 && gy <= z.y2) {
          return { kind: "zone", id: z.id };
        }
      }
      const spawnIdx = layout.spawnPoints.findIndex((p) => p.gx === gx && p.gy === gy);
      if (spawnIdx >= 0) return { kind: "spawn", index: spawnIdx };
      return null;
    },
    [layout],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    const cell = toGrid(e.clientX, e.clientY);
    if (!cell) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);

    // Resize handle is a separate element — handled there.
    if ((e.target as HTMLElement).dataset?.resizeHandle === "se") return;

    if (palette) {
      if (palette.type === "office") {
        onEdit(placeOffice(layout, cell.gx, cell.gy));
      } else if (palette.type === "zone") {
        onEdit(placeZone(layout, palette.zoneTemplate, cell.gx, cell.gy));
      } else if (palette.type === "furniture") {
        onEdit(placeFurniture(layout, palette.furnitureKind, cell.gx, cell.gy));
      } else if (palette.type === "building") {
        onEdit(
          placeOutdoorBuilding(layout, {
            style: palette.style,
            label: palette.label,
            color: palette.color,
            w: palette.w,
            d: palette.d,
            toMapId: palette.toMapId,
            gx: cell.gx,
            gy: cell.gy,
            doorWall: "S",
          }),
        );
      } else if (palette.type === "transition") {
        onEdit(placeTransition(layout, cell.gx, cell.gy, palette.toMapId, palette.label));
      } else if (palette.type === "spawn") {
        onEdit(placeSpawn(layout, cell.gx, cell.gy));
      }
      return;
    }

    const hit = hitTest(cell.gx, cell.gy);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;

    if (!hit) {
      if (!additive) onSelect([]);
      setDrag({
        type: "marquee",
        startGx: cell.gx,
        startGy: cell.gy,
        endGx: cell.gx,
        endGy: cell.gy,
        additive,
      });
      return;
    }

    let nextSel: EditorSelectionSet;
    if (additive) {
      if (isSelected(selection, hit)) {
        nextSel = selection.filter((s) => !sameItemRef(s, hit));
      } else {
        nextSel = [...selection, hit];
      }
    } else if (isSelected(selection, hit)) {
      nextSel = selection;
    } else {
      // Selecting a building expands linked door/transition into the drag group.
      nextSel = expandBuildingGroup(layout, [hit]);
    }
    onSelect(nextSel);

    const moveRefs =
      nextSel.length > 0
        ? expandBuildingGroup(layout, nextSel)
        : expandBuildingGroup(layout, [hit]);
    setDrag({
      type: "move",
      refs: moveRefs,
      lastGx: cell.gx,
      lastGy: cell.gy,
      startLayout: layout,
      dirty: false,
    });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const cell = toGrid(e.clientX, e.clientY);
    setHover(cell);
    if (!drag) return;

    if (drag.type === "marquee") {
      if (!cell) return;
      setDrag({ ...drag, endGx: cell.gx, endGy: cell.gy });
      return;
    }

    if (drag.type === "resize") {
      if (!cell) return;
      const next = resizeItemSe(drag.startLayout, drag.ref, cell.gx, cell.gy);
      onLiveChange(next);
      setDrag({ ...drag, dirty: true });
      return;
    }

    // move
    if (!cell) return;
    const dx = cell.gx - drag.lastGx;
    const dy = cell.gy - drag.lastGy;
    if (dx === 0 && dy === 0) return;
    const next = moveItems(layout, drag.refs, dx, dy);
    onLiveChange(next);
    setDrag({ ...drag, lastGx: cell.gx, lastGy: cell.gy, dirty: true });
  };

  const onPointerUp = () => {
    if (!drag) return;
    if (drag.type === "marquee") {
      const found = itemsInRect(layout, drag.startGx, drag.startGy, drag.endGx, drag.endGy);
      if (drag.additive) {
        const keys = new Set(
          selection.map((s) => `${s.kind}:${s.kind === "spawn" ? s.index : s.id}`),
        );
        const merged = [...selection];
        for (const f of found) {
          const k = `${f.kind}:${f.kind === "spawn" ? f.index : f.id}`;
          if (!keys.has(k)) {
            keys.add(k);
            merged.push(f);
          }
        }
        onSelect(merged);
      } else {
        onSelect(found);
      }
    } else if (drag.dirty) {
      // Final layout already applied via onChange; parent records undo from startLayout.
      onGestureEnd?.(drag.startLayout);
    }
    setDrag(null);
  };

  const onResizePointerDown = (e: React.PointerEvent, ref: EditorItemRef) => {
    e.stopPropagation();
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    onSelect([ref]);
    setDrag({ type: "resize", ref, startLayout: layout, dirty: false });
  };

  const width = layout.cols * CELL;
  const height = layout.rows * CELL;

  const primary = selection.length === 1 ? selection[0] : null;
  const canResize =
    primary &&
    (primary.kind === "office" || primary.kind === "zone" || primary.kind === "furniture");
  const resizeBounds = canResize && primary ? getItemBounds(layout, primary) : null;

  const marqueeStyle =
    drag?.type === "marquee"
      ? (() => {
          const x1 = Math.min(drag.startGx, drag.endGx);
          const y1 = Math.min(drag.startGy, drag.endGy);
          const x2 = Math.max(drag.startGx, drag.endGx);
          const y2 = Math.max(drag.startGy, drag.endGy);
          return {
            left: x1 * CELL,
            top: y1 * CELL,
            width: (x2 - x1 + 1) * CELL,
            height: (y2 - y1 + 1) * CELL,
          };
        })()
      : null;

  const ghost =
    palette && hover
      ? (() => {
          const size = paletteGhostSize(palette);
          return {
            left: hover.gx * CELL,
            top: hover.gy * CELL,
            width: size.w * CELL,
            height: size.h * CELL,
          };
        })()
      : null;

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-border bg-[#0A1A3A]">
      <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-2 py-1 text-[10px] text-white/70">
        <span>
          Top = <strong className="text-white">N</strong> · Right ={" "}
          <strong className="text-white">E</strong>
          {selection.length > 1 ? (
            <span className="ml-2 text-amber-200">{selection.length} selected</span>
          ) : null}
        </span>
        <span className="font-mono">
          {hover ? `cursor ${hover.gx},${hover.gy}` : `${layout.cols}×${layout.rows}`}
        </span>
      </div>
      <div ref={wrapRef} className="relative min-h-0 flex-1 overflow-auto">
        <div
          className="relative cursor-crosshair"
          style={{ width, height }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={() => setHover(null)}
        >
          <div className="pointer-events-none absolute left-1 top-1 z-10 rounded bg-black/50 px-1 text-[9px] text-cyan-200">
            NW
          </div>
          <div className="pointer-events-none absolute right-1 top-1 z-10 rounded bg-black/50 px-1 text-[9px] text-amber-200">
            NE
          </div>

          <svg
            width={width}
            height={height}
            className="pointer-events-none absolute inset-0"
            aria-hidden="true"
            focusable="false"
            role="presentation"
          >
            {Array.from({ length: layout.cols + 1 }, (_, col) => col).map((col) => (
              <line
                key={`grid-col-${col}`}
                x1={col * CELL}
                y1={0}
                x2={col * CELL}
                y2={height}
                stroke="rgba(255,255,255,0.04)"
              />
            ))}
            {Array.from({ length: layout.rows + 1 }, (_, row) => row).map((row) => (
              <line
                key={`grid-row-${row}`}
                x1={0}
                y1={row * CELL}
                x2={width}
                y2={row * CELL}
                stroke="rgba(255,255,255,0.04)"
              />
            ))}
            {Array.from({ length: Math.floor(layout.cols / 10) + 1 }, (_, i) => i * 10).map(
              (col) =>
                col < layout.cols ? (
                  <text
                    key={`tx-${col}`}
                    x={col * CELL + 2}
                    y={10}
                    fill="rgba(255,255,255,0.25)"
                    fontSize={8}
                  >
                    {col}
                  </text>
                ) : null,
            )}
            {Array.from({ length: Math.floor(layout.rows / 10) + 1 }, (_, i) => i * 10).map(
              (row) =>
                row > 0 && row < layout.rows ? (
                  <text
                    key={`ty-${row}`}
                    x={2}
                    y={row * CELL + 10}
                    fill="rgba(255,255,255,0.25)"
                    fontSize={8}
                  >
                    {row}
                  </text>
                ) : null,
            )}
          </svg>

          {ghost ? (
            <div
              className="pointer-events-none absolute z-20 border-2 border-dashed border-sky-300/80 bg-sky-400/20"
              style={ghost}
            />
          ) : null}

          {layout.commonZones.map((z) => {
            const ref: EditorItemRef = { kind: "zone", id: z.id };
            const selected = isSelected(selection, ref);
            const broken = hasIssue(ref);
            return (
              <div
                key={z.id}
                className={cn(
                  "absolute border text-[9px] font-medium leading-tight text-white/90",
                  z.priv
                    ? "border-purple-300/50 bg-purple-500/40"
                    : "border-cyan-300/40 bg-cyan-500/30",
                  selected && "ring-2 ring-amber-300",
                  broken && !selected && "ring-2 ring-red-400",
                )}
                style={{
                  left: z.x1 * CELL,
                  top: z.y1 * CELL,
                  width: (z.x2 - z.x1 + 1) * CELL,
                  height: (z.y2 - z.y1 + 1) * CELL,
                }}
                title={z.name}
              >
                <span className="block truncate px-0.5">{z.name}</span>
              </div>
            );
          })}

          {layout.offices.map((o) => {
            const ref: EditorItemRef = { kind: "office", id: o.slotKey };
            const selected = isSelected(selection, ref);
            const broken = hasIssue(ref);
            return (
              <div
                key={o.slotKey}
                className={cn(
                  "absolute border border-blue-300/50 bg-blue-500/45 text-[9px] font-medium text-white",
                  selected && "ring-2 ring-amber-300",
                  broken && !selected && "ring-2 ring-red-400",
                )}
                style={{
                  left: o.x1 * CELL,
                  top: o.y1 * CELL,
                  width: (o.x2 - o.x1 + 1) * CELL,
                  height: (o.y2 - o.y1 + 1) * CELL,
                }}
                title={o.nameDefault}
              >
                <span className="block truncate px-0.5">{o.nameDefault}</span>
                <span
                  className="absolute h-1.5 w-1.5 rounded-full bg-emerald-300"
                  style={{
                    left: (o.door.gx - o.x1) * CELL + CELL / 2 - 3,
                    top: (o.door.gy - o.y1) * CELL + CELL / 2 - 3,
                  }}
                />
              </div>
            );
          })}

          {layout.furniture.map((f) => {
            const ref: EditorItemRef = { kind: "furniture", id: f.id };
            const selected = isSelected(selection, ref);
            const broken = hasIssue(ref);
            const isBldg = f.kind === "building";
            const isDoor = f.kind === "door";
            const label = f.displayName || f.style || f.kind;
            return (
              <div
                key={f.id}
                className={cn(
                  "absolute flex items-center justify-center overflow-hidden rounded-sm border text-[8px] capitalize text-white",
                  isBldg
                    ? "border-white/30 font-semibold"
                    : isDoor
                      ? "border-emerald-300/60 bg-emerald-500/50"
                      : "border-amber-200/40 bg-amber-500/50",
                  selected && "ring-2 ring-amber-200",
                  broken && !selected && "ring-2 ring-red-400",
                )}
                style={{
                  left: f.gx * CELL,
                  top: f.gy * CELL,
                  width: Math.max(CELL, f.w * CELL),
                  height: Math.max(CELL, f.d * CELL),
                  background: isBldg ? `${f.c ?? "#5A7A9A"}cc` : undefined,
                }}
                title={`${f.kind}${f.style ? ` · ${f.style}` : ""} @${f.gx},${f.gy} ${f.w}×${f.d}${f.doorWall ? ` door ${f.doorWall}` : ""}`}
              >
                <span className="block max-w-full truncate px-0.5 text-center leading-tight">
                  {isBldg || isDoor ? label : f.kind.slice(0, 4)}
                </span>
                {isBldg && f.doorWall ? (
                  <span className="absolute bottom-0 right-0 rounded-tl bg-black/50 px-0.5 text-[7px]">
                    {f.doorWall}
                  </span>
                ) : null}
              </div>
            );
          })}

          {(layout.transitions ?? []).map((t) => {
            const ref: EditorItemRef = { kind: "transition", id: t.id };
            const selected = isSelected(selection, ref);
            const broken = hasIssue(ref);
            return (
              <div
                key={t.id}
                className={cn(
                  "absolute flex items-center justify-center rounded border-2 border-fuchsia-300 bg-fuchsia-500/70 text-[8px] font-bold text-white",
                  selected && "ring-2 ring-amber-300",
                  broken && !selected && "ring-2 ring-red-400",
                )}
                style={{
                  left: t.gx * CELL + 1,
                  top: t.gy * CELL + 1,
                  width: CELL - 2,
                  height: CELL - 2,
                }}
                title={`→ ${t.toMapId} (${t.label ?? ""})`}
              >
                →
              </div>
            );
          })}

          {layout.spawnPoints.map((p) => {
            // Prefer coordinate key; if two spawns share a tile, indexOf still distinguishes for selection.
            const spawnIndex = layout.spawnPoints.findIndex(
              (s) => s === p || (s.gx === p.gx && s.gy === p.gy),
            );
            const ref: EditorItemRef = { kind: "spawn", index: spawnIndex };
            const selected = isSelected(selection, ref);
            const broken = hasIssue(ref);
            return (
              <div
                key={`spawn-${p.gx}x${p.gy}`}
                className={cn(
                  "absolute rounded-full border-2 border-white bg-emerald-400",
                  selected && "ring-2 ring-amber-300",
                  broken && !selected && "ring-2 ring-red-400",
                )}
                style={{
                  left: p.gx * CELL + 2,
                  top: p.gy * CELL + 2,
                  width: CELL - 4,
                  height: CELL - 4,
                }}
                title={`Spawn ${spawnIndex + 1}`}
              />
            );
          })}

          {marqueeStyle ? (
            <div
              className="pointer-events-none absolute z-30 border border-dashed border-sky-300 bg-sky-400/15"
              style={marqueeStyle}
            />
          ) : null}

          {resizeBounds && primary ? (
            <button
              type="button"
              data-resize-handle="se"
              aria-label="Resize"
              className="absolute z-40 h-3 w-3 cursor-se-resize rounded-sm border border-white bg-amber-400 shadow"
              style={{
                left: (resizeBounds.x2 + 1) * CELL - 6,
                top: (resizeBounds.y2 + 1) * CELL - 6,
              }}
              onPointerDown={(e) => onResizePointerDown(e, primary)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
